import { parse as parseDuration } from 'iso8601-duration';
import { isEmpty, isNil } from 'lodash';
import { ListItem } from '@shared/scenes/workflows/edit/components/optionsMenu/referencedInOptions/SelectableItemList';
import { I18nKey, i18n } from '@shared/util/i18n';

export const TIME_OF_EVENT_DURATION = 'PT0M0S';
export const DEFAULT_COUNT = 1;
export const DEFAULT_COUNT_STR = String(DEFAULT_COUNT);

interface ParsedDuration {
  number: string;
  unit: string;
}

export function parseDurationString(duration?: string): ParsedDuration | null {
  if (isNil(duration)) {
    return null;
  }
  if (duration === TIME_OF_EVENT_DURATION) {
    return { number: '0', unit: TIME_OF_EVENT_DURATION };
  }

  const isNegative = duration.startsWith('-');
  const str = !isNegative ? duration : duration.slice(1);
  const matches = parseDuration(str);

  // PnD
  if (str.includes('D')) {
    return { number: String(matches.days), unit: isNegative ? '-PnD' : 'PnD' };
  }

  // PnH
  if (str.includes('H')) {
    return { number: String(matches.hours), unit: isNegative ? '-PTnH' : 'PTnH' };
  }

  // PTnM
  const minuteRegex = /PT\d+M/;
  if (minuteRegex.test(str)) {
    return { number: String(matches.minutes), unit: isNegative ? '-PTnM' : 'PTnM' };
  }

  return null;
}

export const unitListItems: ListItem[] = [
  {
    id: TIME_OF_EVENT_DURATION,
    label: 'builder.scheduledAutomation.units.timeOfEvent',
    labelWithCount: 'builder.scheduledAutomation.units.timeOfEvent',
  },
  {
    id: '-PTnM',
    label: 'builder.scheduledAutomation.units.minutesBefore',
    labelWithCount: 'builder.scheduledAutomation.unitsWithCount.minutesBefore',
  },
  {
    id: '-PTnH',
    label: 'builder.scheduledAutomation.units.hoursBefore',
    labelWithCount: 'builder.scheduledAutomation.unitsWithCount.hoursBefore',
  },
  {
    id: '-PnD',
    label: 'builder.scheduledAutomation.units.daysBefore',
    labelWithCount: 'builder.scheduledAutomation.unitsWithCount.daysBefore',
  },
  {
    id: 'PTnM',
    label: 'builder.scheduledAutomation.units.minutesAfter',
    labelWithCount: 'builder.scheduledAutomation.unitsWithCount.minutesAfter',
  },
  {
    id: 'PTnH',
    label: 'builder.scheduledAutomation.units.hoursAfter',
    labelWithCount: 'builder.scheduledAutomation.unitsWithCount.hoursAfter',
  },
  {
    id: 'PnD',
    label: 'builder.scheduledAutomation.units.daysAfter',
    labelWithCount: 'builder.scheduledAutomation.unitsWithCount.daysAfter',
  },
];

/*
  Removes manually entered 'time of event' durations in favor of TIME_OF_EVENT_DURATION.
  Also removes general duplicates.
*/
const timeOfEventDuplicates = ['PT0M', 'PT0H', 'P0D', '-PT0M', '-PT0H', '-P0D'];
export function dedupeDurations(durations: string[]) {
  const newDurations = [...durations];
  const durationsContainsTimeOfEvent = durations.some(v => timeOfEventDuplicates.includes(v));
  if (durationsContainsTimeOfEvent) {
    newDurations.unshift(TIME_OF_EVENT_DURATION);
  }
  const filteredDurations = newDurations.filter((v, i) => {
    if (timeOfEventDuplicates.includes(v)) {
      return false;
    }
    return newDurations.indexOf(v) === i;
  });
  return filteredDurations;
}

export function humanReadableDuration(duration: string): string {
  const parsedDuration = parseDurationString(duration);

  if (isNil(parsedDuration)) {
    return '';
  }

  const unitLabelKey = unitListItems?.find(u => u.id === parsedDuration.unit)?.labelWithCount ?? '';
  return i18n(unitLabelKey as I18nKey, {
    count: !isEmpty(parsedDuration.number) ? Number(parsedDuration.number) : 0,
  });
}
